import { useRef, useState } from 'react';
import { Upload, Loader2, LayoutGrid } from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';
import { mediaApi } from '../../api/client';
import MediaLibraryModal from './MediaLibraryModal';

export default function SettingsPanel() {
  const story = useEditorStore((s) => s.story);
  const updateStoryMetadata = useEditorStore((s) => s.updateStoryMetadata);

  const [uploading, setUploading] = useState({});
  const [libraryModal, setLibraryModal] = useState({ open: false, field: null, filter: 'all' });

  const logoRef = useRef(null);
  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const audioRef = useRef(null);

  async function handleUpload(file, field) {
    if (!file) return;
    setUploading(prev => ({ ...prev, [field]: true }));
    try {
      const asset = await mediaApi.upload(file, story?.id);
      updateStoryMetadata({ [field]: asset.url });
      toast.success('File uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  }

  function openLibrary(field, filter, allowedTypes) {
    setLibraryModal({ open: true, field, filter: filter ?? 'all', allowedTypes: allowedTypes ?? null });
  }

  function handleLibrarySelect(url) {
    if (libraryModal.field) updateStoryMetadata({ [libraryModal.field]: url });
    setLibraryModal({ open: false, field: null, filter: 'all' });
  }

  if (!story?.metadata) return null;
  const meta = story.metadata;

  return (
    <div className="p-2">
      <h3 className="panel-label mb-3">Story Settings</h3>

      <div className="space-y-3">
        <Field label="Story Title" required>
          <input
            type="text"
            value={meta.title || ''}
            onChange={(e) => updateStoryMetadata({ title: e.target.value })}
            className="input-field"
            placeholder="My Amazing Story"
          />
        </Field>

        <Field label="Publisher Name" required>
          <input
            type="text"
            value={meta.publisherName || ''}
            onChange={(e) => updateStoryMetadata({ publisherName: e.target.value })}
            className="input-field"
            placeholder="Your Publisher Name"
          />
        </Field>

        <Field label="Description">
          <textarea
            value={meta.description || ''}
            onChange={(e) => updateStoryMetadata({ description: e.target.value })}
            className="input-field resize-none"
            rows={3}
            placeholder="Describe your story..."
          />
        </Field>

        <Field label="Canonical URL">
          <input
            type="url"
            value={meta.canonicalUrl || ''}
            onChange={(e) => updateStoryMetadata({ canonicalUrl: e.target.value })}
            className="input-field"
            placeholder="https://example.com/story"
          />
        </Field>

        <Field label="Publisher Logo URL">
          <input
            ref={logoRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => { handleUpload(e.target.files?.[0], 'publisherLogoSrc'); e.target.value = ''; }}
          />
          <MediaButtons
            onUpload={() => logoRef.current?.click()}
            onLibrary={() => openLibrary('publisherLogoSrc', 'image', ['image'])}
            uploading={uploading.publisherLogoSrc}
          />
          <input
            type="url"
            value={meta.publisherLogoSrc || ''}
            onChange={(e) => updateStoryMetadata({ publisherLogoSrc: e.target.value })}
            className="input-field"
            placeholder="https://example.com/logo.png"
          />
        </Field>

        <Field label="Portrait Poster URL" hint="3:4 ratio, recommended 640×853px">
          <input
            ref={portraitRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => { handleUpload(e.target.files?.[0], 'posterPortraitSrc'); e.target.value = ''; }}
          />
          <MediaButtons
            onUpload={() => portraitRef.current?.click()}
            onLibrary={() => openLibrary('posterPortraitSrc', 'all', ['image', 'video'])}
            uploading={uploading.posterPortraitSrc}
          />
          <input
            type="url"
            value={meta.posterPortraitSrc || ''}
            onChange={(e) => updateStoryMetadata({ posterPortraitSrc: e.target.value })}
            className="input-field"
            placeholder="https://example.com/poster.jpg"
          />
        </Field>

        <Field label="Landscape Poster URL" hint="4:3 ratio">
          <input
            ref={landscapeRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => { handleUpload(e.target.files?.[0], 'posterLandscapeSrc'); e.target.value = ''; }}
          />
          <MediaButtons
            onUpload={() => landscapeRef.current?.click()}
            onLibrary={() => openLibrary('posterLandscapeSrc', 'all', ['image', 'video'])}
            uploading={uploading.posterLandscapeSrc}
          />
          <input
            type="url"
            value={meta.posterLandscapeSrc || ''}
            onChange={(e) => updateStoryMetadata({ posterLandscapeSrc: e.target.value })}
            className="input-field"
            placeholder="https://example.com/poster-ls.jpg"
          />
        </Field>

        <Field label="Background Music">
          <input
            ref={audioRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={e => { handleUpload(e.target.files?.[0], 'backgroundAudioSrc'); e.target.value = ''; }}
          />
          <MediaButtons
            onUpload={() => audioRef.current?.click()}
            onLibrary={() => openLibrary('backgroundAudioSrc', 'audio', ['audio'])}
            uploading={uploading.backgroundAudioSrc}
          />
          <input
            type="url"
            value={meta.backgroundAudioSrc || ''}
            onChange={(e) => updateStoryMetadata({ backgroundAudioSrc: e.target.value })}
            className="input-field text-xs"
            placeholder="Or paste URL: https://example.com/audio.mp3"
          />
          {meta.backgroundAudioSrc && (
            <p className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--t-muted)' }}>
              {meta.backgroundAudioSrc}
            </p>
          )}
          <label className="flex items-center gap-2 mt-1.5">
            <input
              type="checkbox"
              checked={meta.backgroundAudioLoop !== false}
              onChange={(e) => updateStoryMetadata({ backgroundAudioLoop: e.target.checked })}
              className="rounded"
            />
            <span className="text-xs" style={{ color: 'var(--t-muted)' }}>Loop audio</span>
          </label>
        </Field>

        <Field label="Language">
          <input
            type="text"
            value={meta.language || 'en'}
            onChange={(e) => updateStoryMetadata({ language: e.target.value })}
            className="input-field"
            placeholder="en"
          />
        </Field>

        <Field label="Categories" hint="Comma separated">
          <CommaSeparatedInput
            value={meta.categories || []}
            onChange={(v) => updateStoryMetadata({ categories: v })}
            placeholder="travel, lifestyle, news"
          />
        </Field>

        <Field label="Tags" hint="Comma separated">
          <CommaSeparatedInput
            value={meta.tags || []}
            onChange={(v) => updateStoryMetadata({ tags: v })}
            placeholder="amp, web story, google"
          />
        </Field>
      </div>

      <MediaLibraryModal
        isOpen={libraryModal.open}
        onClose={() => setLibraryModal({ open: false, field: null, filter: 'all', allowedTypes: null })}
        onSelect={handleLibrarySelect}
        defaultFilter={libraryModal.filter}
        allowedTypes={libraryModal.allowedTypes}
      />
    </div>
  );
}

function MediaButtons({ onUpload, onLibrary, uploading }) {
  return (
    <div className="flex gap-1.5 mb-1.5">
      <button
        onClick={onUpload}
        disabled={uploading}
        className="flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all disabled:opacity-50 flex-1"
        style={{ background: 'var(--t-panel)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
      >
        {uploading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
        Upload
      </button>
      <button
        onClick={onLibrary}
        className="flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all flex-1"
        style={{ background: 'var(--t-panel)', color: 'var(--t-text)', border: '1px solid var(--t-border)' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.color = 'var(--brand-primary)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--t-border)'; e.currentTarget.style.color = 'var(--t-text)'; }}
      >
        <LayoutGrid size={10} />
        From Library
      </button>
    </div>
  );
}

function CommaSeparatedInput({ value, onChange, placeholder }) {
  const [raw, setRaw] = useState(null); // null = not focused

  const display = raw !== null ? raw : value.join(', ');

  function handleFocus() {
    setRaw(value.join(', '));
  }

  function handleChange(e) {
    setRaw(e.target.value);
  }

  function handleBlur() {
    const parsed = raw.split(',').map((s) => s.trim()).filter(Boolean);
    onChange(parsed);
    setRaw(null);
  }

  return (
    <input
      type="text"
      value={display}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      className="input-field"
      placeholder={placeholder}
    />
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--t-muted)' }}>
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
        {hint && <span className="font-normal ml-1" style={{ color: 'var(--t-muted)' }}>({hint})</span>}
      </label>
      {children}
    </div>
  );
}
